import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CaseStudyService } from '@/services/supabase/caseStudies';
import { CaseStudyItem, CaseStudySection, CaseStudyImage, CaseStudyCreationMethod } from '@/types/case-study';
import { useAuth } from '@/hooks/domains/auth/useAuth';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';

export function useCaseStudy() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Case Study CRUD Operations
  const createCaseStudy = useMutation({
    mutationFn: async (data: { 
      title: string; 
      description?: string; 
      cover_image?: string; 
      status?: 'draft' | 'published';
      slug?: string;
      seo_title?: string;
      seo_description?: string;
      og_image?: string;
      sections?: CaseStudySection[];
      creation_method?: CaseStudyCreationMethod;
    }) => {
      if (!user) throw new Error('User not authenticated');
      return CaseStudyService.createCaseStudy(data, user.id);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['case-studies'] });
      queryClient.invalidateQueries({ queryKey: ['unified-portfolio'] });
      const status = data?.status;
      const message =
        status === 'draft'
          ? 'Case study saved as draft successfully'
          : status === 'published'
            ? 'Case study published successfully'
            : 'Case study created successfully';
      toast.success(message);
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to create case study');
    },
  });

  const updateCaseStudy = useMutation({
    mutationFn: CaseStudyService.updateCaseStudy,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['case-studies'] });
      queryClient.invalidateQueries({ queryKey: ['unified-portfolio'] });
      // Also invalidate the specific case study query
      if (data?.id) {
        queryClient.invalidateQueries({ queryKey: ['case-study', data.id] });
      }
      const status = data?.status;
      const message =
        status === 'draft'
          ? 'Case study saved as draft successfully'
          : status === 'published'
            ? 'Case study published successfully'
            : 'Case study updated successfully';
      toast.success(message);
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update case study');
    },
  });

  const deleteCaseStudy = useMutation({
    mutationFn: CaseStudyService.deleteCaseStudy,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['case-studies'] });
      queryClient.invalidateQueries({ queryKey: ['unified-portfolio'] });
      toast.success('Case study deleted successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to delete case study');
    },
  });

  const publishCaseStudy = useMutation({
    mutationFn: async (id: string) => {
      return CaseStudyService.updateCaseStudy({ id, status: 'published' });
    },
    onSuccess: (data, id) => {
      queryClient.invalidateQueries({ queryKey: ['case-studies'] });
      queryClient.invalidateQueries({ queryKey: ['unified-portfolio'] });
      // Also invalidate the specific case study query
      queryClient.invalidateQueries({ queryKey: ['case-study', id] });
      toast.success('Case study published successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to publish case study');
    },
  });

  const unpublishCaseStudy = useMutation({
    mutationFn: async (id: string) => {
      return CaseStudyService.updateCaseStudy({ id, status: 'draft', is_featured: false });
    },
    onSuccess: (data, id) => {
      queryClient.invalidateQueries({ queryKey: ['case-studies'] });
      queryClient.invalidateQueries({ queryKey: ['unified-portfolio'] });
      // Also invalidate the specific case study query
      queryClient.invalidateQueries({ queryKey: ['case-study', id] });
      toast.success('Case study moved to draft successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to unpublish case study');
    },
  });

  // Section Management (Simplified - now works with JSONB)
  const addSection = useMutation({
    mutationFn: async ({ caseStudyId, section }: { caseStudyId: string; section: CaseStudySection }) => {
      return CaseStudyService.addSection(caseStudyId, section);
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['case-studies'] });
      queryClient.invalidateQueries({ queryKey: ['case-study', variables.caseStudyId] });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to add section');
    },
  });

  const updateSection = useMutation({
    mutationFn: async ({ caseStudyId, sectionId, updates }: { 
      caseStudyId: string; 
      sectionId: string; 
      updates: Partial<CaseStudySection> 
    }) => {
      return CaseStudyService.updateSection(caseStudyId, sectionId, updates);
    },
    onSuccess: (data, variables) => {
      // Invalidate both the case studies list and the specific case study
      queryClient.invalidateQueries({ queryKey: ['case-studies'] });
      queryClient.invalidateQueries({ queryKey: ['case-study', variables.caseStudyId] });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update section');
    },
  });

  const deleteSection = useMutation({
    mutationFn: async ({ caseStudyId, sectionId }: { caseStudyId: string; sectionId: string }) => {
      return CaseStudyService.deleteSection(caseStudyId, sectionId);
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['case-studies'] });
      queryClient.invalidateQueries({ queryKey: ['case-study', variables.caseStudyId] });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to delete section');
    },
  });

  const reorderSections = useMutation({
    mutationFn: async ({ caseStudyId, sections }: { caseStudyId: string; sections: CaseStudySection[] }) => {
      return CaseStudyService.reorderSections(caseStudyId, sections);
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['case-studies'] });
      queryClient.invalidateQueries({ queryKey: ['case-study', variables.caseStudyId] });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to reorder sections');
    },
  });

  // Update entire sections array (useful for bulk operations)
  const updateSections = useMutation({
    mutationFn: async ({ caseStudyId, sections }: { caseStudyId: string; sections: CaseStudySection[] }) => {
      return CaseStudyService.updateSections(caseStudyId, sections);
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['case-studies'] });
      queryClient.invalidateQueries({ queryKey: ['case-study', variables.caseStudyId] });
      toast.success('Sections updated successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update sections');
    },
  });

  return {
    // Case Study operations
    createCaseStudy,
    updateCaseStudy,
    deleteCaseStudy,
    publishCaseStudy,
    unpublishCaseStudy,
    
    // Section operations
    addSection,
    updateSection,
    deleteSection,
    reorderSections,
    updateSections, // NEW: Update entire sections array
  };
}

// Hook for fetching case studies
export function useCaseStudies(profileId?: string, status?: 'draft' | 'published', options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['case-studies', profileId, status],
    queryFn: () => CaseStudyService.listCaseStudies(profileId, status),
    enabled: options?.enabled !== undefined ? options.enabled : !!profileId,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
}

// Hook for fetching a single case study
export function useCaseStudyById(id: string) {
  return useQuery({
    queryKey: ['case-study', id],
    queryFn: async () => {
      const result = await CaseStudyService.getCaseStudy(id);
      if (result.sections) {
        result.sections.forEach((section, index) => {

        });
      }
      return result;
    },
    enabled: !!id,
    staleTime: 30000, // Keep data fresh for 30 seconds to reduce unnecessary refetches
    refetchOnWindowFocus: false, // Don't refetch when window regains focus
    retry: async (failureCount, error: any) => {
      // Don't retry more than twice
      if (failureCount >= 2) return false;

      // Check if error might be due to session expiration
      const errorMessage = error?.message?.toLowerCase() || '';
      const isSessionError = errorMessage.includes('not found') ||
                           errorMessage.includes('unauthorized') ||
                           errorMessage.includes('forbidden');

      if (isSessionError) {
        console.log(`[useCaseStudyById] Retry ${failureCount + 1}/2 - Attempting session refresh...`);

        try {
          // Try to refresh the session
          const { data, error: refreshError } = await supabase.auth.refreshSession();

          if (refreshError || !data.session) {
            console.error('[useCaseStudyById] Session refresh failed:', refreshError);
            return false; // Don't retry if refresh fails
          }

          console.log('[useCaseStudyById] Session refreshed, retrying query...');
          // Wait a bit before retrying
          await new Promise(resolve => setTimeout(resolve, 1000));
          return true; // Retry the query
        } catch (err) {
          console.error('[useCaseStudyById] Error during session refresh:', err);
          return false;
        }
      }

      // For other errors, retry with exponential backoff
      await new Promise(resolve => setTimeout(resolve, Math.min(1000 * 2 ** failureCount, 5000)));
      return true;
    },
  });
}
